import React, { useState } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    updateProfile
} from 'firebase/auth';
import { auth, googleProvider, functions } from '../firebase';
import { signInWithPopup } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { saveUserToFirestore, checkUsernameAvailable } from '../dbFunctions';
import { useNavigate } from 'react-router-dom';
import './AuthModal.css';

const AuthModal = ({ isOpen, onClose, initialMode = 'signin' }) => {
    const navigate = useNavigate();
    const [mode, setMode] = useState(initialMode); // signin, register, forgot, verify-email, reset-password-code, reset-password-new
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [usernameStatus, setUsernameStatus] = useState('');

    // Form States
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [nickname, setNickname] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);

    // Verification Code State
    const [verificationCode, setVerificationCode] = useState('');
    const [newResetPassword, setNewResetPassword] = useState('');

    const resetForm = () => {
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setNickname('');
        setVerificationCode('');
        setNewResetPassword('');
        setError('');
        setSuccess('');
        setUsernameStatus('');
        setTermsAccepted(false);
    };

    const switchMode = (newMode) => {
        setError('');
        setSuccess('');
        setMode(newMode);
    };

    const handleNicknameChange = async (val) => {
        const cleaned = val.toLowerCase().replace(/[^a-z0-9_]/g, '');
        setNickname(cleaned);

        if (cleaned.length < 3) {
            setUsernameStatus('');
            return;
        }

        setUsernameStatus('checking');
        try {
            const isAvailable = await checkUsernameAvailable(cleaned);
            setUsernameStatus(isAvailable ? 'available' : 'taken');
        } catch (error) {
            console.error("Username check failed", error);
            setUsernameStatus('');
        }
    };

    // 1. REGISTER FLOW
    const handleRegister = async (e) => {
        e.preventDefault();
        setError('');

        // Basic Validation
        if (!nickname.trim() || nickname.length < 3) return setError('Geçerli bir kullanıcı adı girin.');
        if (!email.trim()) return setError('Email adresi gerekli.');
        if (password.length < 8) return setError('Şifre en az 8 karakter olmalı.');
        if (!/[A-Z]/.test(password)) return setError('Şifre en az 1 büyük harf içermeli.');
        if (!/[a-z]/.test(password)) return setError('Şifre en az 1 küçük harf içermeli.');
        if (!/[0-9]/.test(password)) return setError('Şifre en az 1 rakam içermeli.');
        if (password !== confirmPassword) return setError('Şifreler eşleşmiyor.');
        if (!termsAccepted) return setError('Kullanım koşullarını kabul etmelisiniz.');

        const isAvailable = await checkUsernameAvailable(nickname);
        if (!isAvailable) return setError('Bu kullanıcı adı dolu.');

        setLoading(true);
        try {
            // Create user (unverified by default in Firebase)
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            await updateProfile(user, { displayName: nickname });
            await saveUserToFirestore({ ...user, displayName: nickname }, nickname);

            // Send Verification Code
            const sendCode = httpsCallable(functions, 'sendVerificationCode');
            await sendCode({ email: user.email, type: 'register' });

            setSuccess('Kayıt başarılı! Doğrulama kodu gönderildi.');
            setMode('verify-email');
        } catch (err) {
            console.error('Register error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // 2. VERIFY EMAIL CODE
    const handleVerifyEmail = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const verifyCode = httpsCallable(functions, 'verifyEmailCode');
            await verifyCode({ email, code: verificationCode });

            setSuccess('Email doğrulandı! Giriş yapabilirsiniz.');
            setTimeout(() => {
                onClose();
                window.location.reload(); // Refresh to update auth state properly if needed
            }, 1500);
        } catch (err) {
            console.error(err);
            setError('Kod hatalı veya süresi dolmuş.');
        } finally {
            setLoading(false);
        }
    };

    // 3. SEND RESET CODE
    const handleSendResetCode = async (e) => {
        e.preventDefault();
        setError('');
        if (!email) return setError('Email gerekli.');

        setLoading(true);
        try {
            const sendCode = httpsCallable(functions, 'sendVerificationCode');
            await sendCode({ email, type: 'reset' });
            setSuccess('Sıfırlama kodu gönderildi.');
            setMode('reset-verify');
        } catch (err) {
            console.error(err);
            setError('Kod gönderilemedi. Email adresini kontrol et.');
        } finally {
            setLoading(false);
        }
    };

    // 4. RESET PASSWORD WITH CODE
    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError('');
        if (newResetPassword.length < 8) return setError('Yeni şifre en az 8 karakter olmalı.');
        if (!/[A-Z]/.test(newResetPassword)) return setError('Şifre en az 1 büyük harf içermeli.');
        if (!/[a-z]/.test(newResetPassword)) return setError('Şifre en az 1 küçük harf içermeli.');
        if (!/[0-9]/.test(newResetPassword)) return setError('Şifre en az 1 rakam içermeli.');

        setLoading(true);
        try {
            const resetPwd = httpsCallable(functions, 'resetPasswordWithCode');
            await resetPwd({ email, code: verificationCode, newPassword: newResetPassword });
            setSuccess('Şifreniz başarıyla güncellendi! Giriş yapabilirsiniz.');
            setTimeout(() => setMode('signin'), 1500);
        } catch (err) {
            console.error(err);
            setError('Şifre değiştirilemedi. Kod hatalı olabilir.');
        } finally {
            setLoading(false);
        }
    };

    const handleSignIn = async (e) => {
        e.preventDefault();
        setError('');
        if (!email || !password) return setError('Bilgileri eksiksiz girin.');

        setLoading(true);
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            if (!userCredential.user.emailVerified) {
                setError('Lütfen önce email adresinizi doğrulayın.');
                // Option: Offer to resend code?
                // For simplified flow, maybe let them login but show restricted access? 
                // Or force verify here:
                // await auth.signOut();
                // setMode('verify-email'); 
                // But we need to RESEND the code if they lost it.
                // For now, just show error.
            } else {
                onClose();
                navigate('/profile');
            }
        } catch (err) {
            console.error(err);
            setError('Giriş yapılamadı. Bilgilerinizi kontrol edin.');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        try {
            const result = await signInWithPopup(auth, googleProvider);
            await saveUserToFirestore(result.user);
            onClose();
            navigate('/profile');
        } catch (err) {
            console.error(err);
            setError('Google girişi başarısız.');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="auth-modal-overlay" onClick={onClose}>
            <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
                <button className="auth-close-btn" onClick={onClose}>×</button>

                <div className="auth-header">
                    <h2>
                        {mode === 'signin' && 'Giriş Yap'}
                        {mode === 'register' && 'Hesap Oluştur'}
                        {mode === 'forgot' && 'Şifre Sıfırlama'}
                        {mode === 'verify-email' && 'Email Doğrulama'}
                        {mode === 'reset-verify' && 'Yeni Şifre Belirle'}
                    </h2>
                    <p>
                        {mode === 'signin' && 'Hoş geldin! 👋'}
                        {mode === 'register' && 'Hemen aramıza katıl! 🚀'}
                        {mode === 'forgot' && 'Email adresine kod göndereceğiz.'}
                        {mode === 'verify-email' && `${email} adresine gelen kodu gir.`}
                        {mode === 'reset-verify' && 'Kodu ve yeni şifreni gir.'}
                    </p>
                </div>

                {error && <div className="auth-message auth-error">{error}</div>}
                {success && <div className="auth-message auth-success">{success}</div>}

                {/* --- 1. SIGN IN --- */}
                {mode === 'signin' && (
                    <form className="auth-form" onSubmit={handleSignIn}>
                        <div className="auth-input-group">
                            <label>Email</label>
                            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                        </div>
                        <div className="auth-input-group">
                            <label>Şifre</label>
                            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required />
                        </div>
                        <button type="button" className="auth-forgot-link" onClick={() => switchMode('forgot')}>Şifremi Unuttum</button>
                        <button type="submit" className="auth-submit-btn" disabled={loading}>{loading ? '...' : 'Giriş Yap'}</button>
                        <div className="auth-divider"><span>veya</span></div>
                        <button type="button" className="auth-google-btn" onClick={handleGoogleSignIn}>Google ile Giriş</button>
                        <p className="auth-switch">Hesabın yok mu? <button type="button" onClick={() => switchMode('register')}>Kayıt Ol</button></p>
                    </form>
                )}

                {/* --- 2. REGISTER --- */}
                {mode === 'register' && (
                    <form className="auth-form" onSubmit={handleRegister}>
                        <div className="auth-input-group">
                            <label>Kullanıcı Adı</label>
                            <div className="username-input-wrapper">
                                <input
                                    type="text"
                                    value={nickname}
                                    onChange={e => handleNicknameChange(e.target.value)}
                                    className={usernameStatus === 'taken' ? 'input-error' : usernameStatus === 'available' ? 'input-success' : ''}
                                    maxLength={20}
                                />
                                {usernameStatus === 'checking' && <span className="username-status checking">⏳</span>}
                                {usernameStatus === 'available' && <span className="username-status available">✓</span>}
                                {usernameStatus === 'taken' && <span className="username-status taken">✗</span>}
                            </div>
                        </div>
                        <div className="auth-input-group">
                            <label>Email</label>
                            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                        </div>
                        <div className="auth-input-group">
                            <label>Şifre</label>
                            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required />
                        </div>
                        <div className="auth-input-group">
                            <label>Şifre Tekrar</label>
                            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
                        </div>
                        <div className="auth-checkbox-group">
                            <input type="checkbox" id="terms" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} />
                            <label htmlFor="terms">
                                <a href="/kullanim-kosullari" target="_blank" rel="noopener noreferrer">Kullanım Koşulları</a>,{' '}
                                <a href="/gizlilik-politikasi" target="_blank" rel="noopener noreferrer">Gizlilik Politikası</a> ve{' '}
                                <a href="/kvkk" target="_blank" rel="noopener noreferrer">KVKK Aydınlatma Metni</a>'ni okudum, kabul ediyorum.
                            </label>
                        </div>
                        <button type="submit" className="auth-submit-btn" disabled={loading}>{loading ? 'Kayıt Olunuyor...' : 'Kayıt Ol & Doğrula'}</button>
                        <p className="auth-switch">Hesabın var mı? <button type="button" onClick={() => switchMode('signin')}>Giriş Yap</button></p>
                    </form>
                )}

                {/* --- 3. VERIFY EMAIL (Register Step 2) --- */}
                {mode === 'verify-email' && (
                    <form className="auth-form" onSubmit={handleVerifyEmail}>
                        <div className="auth-input-group">
                            <label>Doğrulama Kodu (6 Haneli)</label>
                            <input
                                type="text"
                                placeholder="123456"
                                value={verificationCode}
                                onChange={e => setVerificationCode(e.target.value)}
                                maxLength={6}
                                style={{ letterSpacing: '5px', textAlign: 'center', fontSize: '1.2rem' }}
                                required
                            />
                        </div>
                        <div className="auth-info-text">
                            E-posta gelmediyse lütfen gereksiz (spam) kutusunu kontrol edin.
                        </div>
                        <button type="submit" className="auth-submit-btn" disabled={loading}>{loading ? 'Doğrulanıyor...' : 'Doğrula & Başla'}</button>
                    </form>
                )}

                {/* --- 4. FORGOT PASSWORD (Step 1: Send Code) --- */}
                {mode === 'forgot' && (
                    <form className="auth-form" onSubmit={handleSendResetCode}>
                        <div className="auth-input-group">
                            <label>Email Adresi</label>
                            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                        </div>
                        <button type="submit" className="auth-submit-btn" disabled={loading}>{loading ? 'Gönderiliyor...' : 'Kod Gönder'}</button>
                        <p className="auth-switch"><button type="button" onClick={() => switchMode('signin')}>← Geri Dön</button></p>
                    </form>
                )}

                {/* --- 5. RESET PASSWORD (Step 2: Verify & New Password) --- */}
                {mode === 'reset-verify' && (
                    <form className="auth-form" onSubmit={handleResetPassword}>
                        <div className="auth-input-group">
                            <label>Doğrulama Kodu</label>
                            <input
                                type="text"
                                value={verificationCode}
                                onChange={e => setVerificationCode(e.target.value)}
                                maxLength={6}
                                style={{ letterSpacing: '5px', textAlign: 'center' }}
                                required
                            />
                        </div>
                        <div className="auth-info-text">
                            E-posta gelmediyse lütfen gereksiz (spam) kutusunu kontrol edin.
                        </div>
                        <div className="auth-input-group">
                            <label>Yeni Şifre</label>
                            <input type="password" value={newResetPassword} onChange={e => setNewResetPassword(e.target.value)} required />
                        </div>
                        <button type="submit" className="auth-submit-btn" disabled={loading}>{loading ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}</button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default AuthModal;
